import { IonActionSheet, useIonAlert } from '@ionic/react'
import { arrowForward, search, trashOutline } from 'ionicons/icons'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import { useAppContext } from '../context/AppContext'
import { useStepsContext } from '../context/StepsContext'
import useRequests from '../hooks/useRequests'
import { COMPLETE_INPUT, saveDocumentId } from '../redux/actions'

interface Props {
  data: any
  procedure: 'activation' | 'portability' | 'prospectos' | 'nominativity'
  showActionSheet: boolean
  setShowActionSheet: any
}

const ActionSheet = ({ data, procedure, showActionSheet, setShowActionSheet }: Props) => {
  const [presentAlert] = useIonAlert()
  const { setNewProcedureStep, setSavedDocument }: any = useStepsContext()
  const { setBlockNavigation }: any = useAppContext()
  const { cancelProcedure }: any = useRequests()
  const dispatch = useDispatch()
  const history = useHistory()
  const menuCss = 'procedureMenu'

  const handleCancelProcedure = () => {
    const nominativityMsg = { header: '¿Estás seguro que deseás cancelar el trámite?' }
    const activationMsg = {
      header: 'Cancelar trámite',
      message: `¿Estás seguro que deseás cancelar este trámite? Se eliminarán todos los datos cargados.`
    }
    const message = procedure === 'nominativity' ? nominativityMsg : activationMsg
    const button = procedure === 'nominativity' ? 'Cancelar trámite' : 'Aceptar'
    const styleClass = procedure === 'nominativity' ? 'alert-procedure-nomi' : 'alert-procedure'

    presentAlert({
      ...message,
      cssClass: styleClass,
      buttons: [
        {
          text: button,
          role: 'confirm',
          cssClass: 'logout-alert-btn margin-bottom',
          handler: () => {
            cancelProcedure(data.id)
          }
        },
        {
          text: '',
          role: 'cancel',
          id: 'close-btn',
          cssClass: 'close-alert-btn'
        }
      ]
    })
  }

  const buttonProspectos = [
    {
      text: 'Consultar referido',
      role: 'see-procedure',
      icon: search,
      id: 'see-procedure',
      data: {
        type: 'see'
      },
      handler: () => {
        history.push('/procedures/referidos/detalle')
      }
    }
  ]

  const buttonActivationsInitialized = [
    {
      text: 'Consultar trámite',
      role: 'see-procedure',
      icon: search,
      id: 'see-procedure',
      data: {
        type: 'see'
      },
      handler: () => {
        history.push('/procedures/activaciones/detalle')
      }
    },
    {
      text: 'Reanudar trámite',
      role: 'resume-procedure',
      icon: arrowForward,
      id: 'resume-procedure',
      data: {
        type: 'resume'
      },
      handler: async () => {
        setNewProcedureStep(3)
        setBlockNavigation(true)
        dispatch(saveDocumentId(data.id))
        history.push('/procedures/activaciones/nueva-activacion')
      }
    },
    {
      text: 'Cancelar trámite',
      role: 'cancel-procedure',
      icon: trashOutline,
      id: 'cancel-procedure',
      data: {
        type: 'cancel'
      },
      handler: handleCancelProcedure
    }
  ]

  const buttonActivationsEnded = [
    {
      text: 'Consultar trámite',
      role: 'see-procedure',
      icon: search,
      id: 'see-procedure',
      data: {
        type: 'see'
      },
      handler: () => {
        history.push('/procedures/activaciones/detalle')
      }
    }
  ]

  const buttonNominativityInitialized = [
    {
      text: 'Continuar trámite',
      role: 'resume-procedure',
      icon: arrowForward,
      id: 'resume-procedure',
      data: {
        type: 'resume'
      },
      handler: () => {
        setNewProcedureStep(4)
        setBlockNavigation(true)
        dispatch({
          type: COMPLETE_INPUT,
          payload: {
            name: 'lineNumber',
            value: data.metadata.nom.phone.lineNumber,
            storeForm: 'nomLineForm'
          }
        })
        dispatch(saveDocumentId(data.id))
        history.push('/procedures/nominatividad/nueva-nominatividad')
      }
    },
    {
      text: 'Cancelar trámite',
      role: 'cancel-procedure',
      icon: trashOutline,
      id: 'cancel-procedure',
      data: {
        type: 'cancel'
      },
      handler: handleCancelProcedure
    }
  ]

  const buttonNominativityEnded = [
    {
      text: 'Consultar trámite',
      role: 'see-procedure',
      icon: search,
      id: 'see-procedure',
      data: {
        type: 'see'
      },
      handler: () => {
        history.push('/procedures/nominatividad/detalle')
      }
    }
  ]

  const buttonPortabilityInitialized = [
    {
      text: 'Consultar trámite',
      role: 'continue-procedure',
      icon: search,
      id: 'continue-procedure',
      data: {
        type: 'continue'
      },
      handler: () => {
        history.push('/procedures/portabilidades/detalle')
      }
    },
    {
      text: 'Continuar trámite',
      role: 'continue-procedure',
      icon: arrowForward,
      id: 'continue-procedure',
      data: {
        type: 'continue'
      },
      handler: () => {
        setNewProcedureStep(3)
        setBlockNavigation(true)
        setSavedDocument(true)
        dispatch(saveDocumentId(data.id))
        history.push('/procedures/portabilidades/nueva-portabilidad-py/')
      }
    },
    {
      text: 'Cancelar trámite',
      role: 'cancel-procedure',
      icon: trashOutline,
      id: 'cancel-procedure',
      data: {
        type: 'cancel'
      },
      handler: handleCancelProcedure
    }
  ]

  const buttonPortabilityEnded = [
    {
      text: 'Consultar trámite',
      role: 'continue-procedure',
      icon: search,
      id: 'continue-procedure',
      data: {
        type: 'continue'
      },
      handler: () => {
        history.push('/procedures/portabilidades/detalle')
      }
    }
  ]

  const buttonsRender = () => {
    if (procedure === 'activation') {
      if (data.state === 'INITIALIZED') return buttonActivationsInitialized
      return buttonActivationsEnded
    }
    if (procedure === 'portability') {
      if (data.state === 'INITIALIZED') return buttonPortabilityInitialized
      return buttonPortabilityEnded
    }
    if (procedure === 'nominativity') {
      if (data.state === 'INITIALIZED') return buttonNominativityInitialized
      return buttonNominativityEnded
    }
    return buttonProspectos
  }

  return (
    <IonActionSheet
      mode="md"
      cssClass={menuCss}
      isOpen={showActionSheet}
      onDidDismiss={() => setShowActionSheet(false)}
      header="Seleccionar"
      buttons={[
        ...buttonsRender(),
        {
          text: '',
          id: 'option-close',
          role: 'close',
          handler: () => {
            setShowActionSheet(false)
          }
        }
      ]}
    ></IonActionSheet>
  )
}

export default ActionSheet
